import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface ExpressSignupSuccessPopupProps {
  onContinue: () => void;
}

type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  rotation: number;
  rotationSpeed: number;
  color: string;
  life: number;
};

const COLORS = ['#22c55e', '#4ade80', '#86efac', '#ffffff', '#a3e635', '#fbbf24', '#60a5fa'];

/** PRAIRI brand green */
const SUCCESS_GREEN = '#92d050';
const RUBIK = { fontFamily: 'Rubik, sans-serif' } as const;

/**
 * Success modal after investor express signup.
 * Confetti runs while open so users pause before entering the dashboard.
 */
const ExpressSignupSuccessPopup: React.FC<ExpressSignupSuccessPopupProps> = ({ onContinue }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onContinue();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onContinue]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const particles: Particle[] = [];

    /** Classic party confetti: shoot up/out from a point, then fall — not drip-from-top rain. */
    const spawnCannon = (
      count: number,
      originX: number,
      originY: number,
      angleRad: number,
      spreadRad: number
    ) => {
      for (let i = 0; i < count; i++) {
        const angle = angleRad + (Math.random() - 0.5) * spreadRad;
        const speed = 8 + Math.random() * 12;
        particles.push({
          x: originX + (Math.random() - 0.5) * 24,
          y: originY + (Math.random() - 0.5) * 16,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          size: 5 + Math.random() * 7,
          rotation: Math.random() * Math.PI * 2,
          rotationSpeed: (Math.random() - 0.5) * 0.45,
          color: COLORS[Math.floor(Math.random() * COLORS.length)],
          life: 1,
        });
      }
    };

    const fireCelebration = () => {
      const w = canvas.width;
      const h = canvas.height;
      // Left + right side cannons (upward/inward) and a center burst
      spawnCannon(70, w * 0.15, h * 0.75, -Math.PI / 2.6, Math.PI / 2.2);
      spawnCannon(70, w * 0.85, h * 0.75, -Math.PI + Math.PI / 2.6, Math.PI / 2.2);
      spawnCannon(50, w * 0.5, h * 0.55, -Math.PI / 2, Math.PI * 0.9);
    };

    fireCelebration();
    // A couple of follow-up bursts — still confetti, not continuous rain
    const burstTimers = [
      window.setTimeout(fireCelebration, 450),
      window.setTimeout(fireCelebration, 950),
    ];

    const gravity = 0.22;
    const drag = 0.99;

    const tick = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.vx *= drag;
        p.vy = p.vy * drag + gravity;
        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.rotationSpeed;
        p.life -= 0.0055;

        if (p.life <= 0 || p.y > canvas.height + 40) {
          particles.splice(i, 1);
          continue;
        }

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.globalAlpha = Math.max(0, Math.min(1, p.life));
        ctx.fillStyle = p.color;
        // Slightly thicker rectangles read more like confetti paper than rain
        ctx.fillRect(-p.size / 2, -p.size / 3, p.size, p.size * 0.55);
        ctx.restore();
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('resize', resize);
      burstTimers.forEach(id => window.clearTimeout(id));
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return createPortal(
    <div
      className="fixed inset-0 z-[100000] flex items-center justify-center p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="express-signup-success-title"
      style={RUBIK}
    >
      <div className="absolute inset-0 bg-black/80" />
      <canvas
        ref={canvasRef}
        className="pointer-events-none absolute inset-0 w-full h-full"
        aria-hidden="true"
      />

      {/* Figma Investor LP 1322:1049 — 733×500 */}
      <div
        className="relative z-10 flex w-full max-w-[733px] flex-col items-center justify-center overflow-hidden rounded-2xl px-6 py-8 text-center sm:px-8 sm:py-10"
        style={{
          backgroundColor: '#0F1112',
          ...RUBIK,
          height: 'min(500px, 88vh)',
          minHeight: '360px',
        }}
      >
        <div
          className="pointer-events-none absolute left-[8%] top-[4%] h-10 w-10 rounded-full blur-xl"
          style={{ backgroundColor: SUCCESS_GREEN, opacity: 0.22 }}
          aria-hidden="true"
        />

        <button
          type="button"
          onClick={onContinue}
          className="absolute right-4 top-4 z-20 p-1.5 text-white/80 transition-colors hover:text-white"
          aria-label="Close"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div className="relative flex w-full flex-col items-center">
          <div className="mb-5 flex h-16 w-16 items-center justify-center">
            <svg width="64" height="64" viewBox="0 0 72 72" fill="none" aria-hidden="true">
              <circle
                cx="36"
                cy="36"
                r="30"
                stroke={SUCCESS_GREEN}
                strokeWidth="2.25"
                fill="none"
              />
              <path
                d="M24 37.5L32.5 46L49 28"
                stroke={SUCCESS_GREEN}
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          </div>

          {/* Figma Investor LP 1322:1056 */}
          <div className="w-full text-center" style={RUBIK}>
            <h2
              id="express-signup-success-title"
              className="text-[32px] font-bold leading-tight text-white"
              style={{ ...RUBIK, fontWeight: 700 }}
            >
              Success!
            </h2>
            <p
              className="mt-2 text-[22px] font-normal leading-snug text-white"
              style={{ ...RUBIK, fontWeight: 400 }}
            >
              Your{' '}
              <span style={{ color: SUCCESS_GREEN, ...RUBIK, fontWeight: 400 }}>
                Specially For You
              </span>{' '}
              feed is ready.
            </p>
            <div
              className="mt-8 text-[22px] font-normal leading-snug text-white"
              style={{ ...RUBIK, fontWeight: 400 }}
            >
              <p>
                Review startups{' '}
                <span className="font-bold" style={{ ...RUBIK, fontWeight: 700 }}>
                  anonymously
                </span>
                .
              </p>
              <p>Contact founders when interested.</p>
            </div>
          </div>

          <button
            type="button"
            onClick={onContinue}
            className="mt-8 inline-flex items-center justify-center whitespace-nowrap rounded-full border bg-transparent px-10 py-3 text-[18px] font-normal leading-7 text-white transition-colors hover:bg-[#92d050]/10 focus:outline-none focus:ring-2 focus:ring-[#92d050]/40 sm:mt-10 sm:px-16 sm:py-3.5 sm:text-[20px]"
            style={{ borderColor: SUCCESS_GREEN, ...RUBIK, fontWeight: 400 }}
          >
            Explore Startups
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ExpressSignupSuccessPopup;
